import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AssignmentData } from '../../ABOAssignementTable/ABOAssignementTable';
import { toastMessageType } from '../../../../types/types';

export type FilterKey = Exclude<keyof AssignmentData, 'aboInfo' | 'aboNumber' | 'affiliateCode'>;

export type FilterState = Array<{
  key: FilterKey;
  values: string[];
}>;

export interface FiltersContextValue {
  filterState: FilterState;
  activeFilterState: FilterState[0] | undefined;
  setFilterState: Dispatch<SetStateAction<FilterState>>;
  selectedFilter: FilterKey;
  setSelectedFilter: Dispatch<SetStateAction<FilterKey>>;
  onSelect: (value: string, checked: boolean, code: string) => void;
  setViewDetails: (viewDetails: any) => void;
}

export const initialFilterState: FilterState = [
  {
    key: 'trackingAward',
    values: [],
  },
  {
    key: 'currentAward',
    values: [],
  },
  {
    key: 'primaryAM',
    values: [],
  },
  {
    key: 'caseEvaluator',
    values: [],
  },
  {
    key: 'uplineDiamond',
    values: [],
  },
  {
    key: 'uplineEmerald',
    values: [],
  },
  {
    key: 'amEmerald',
    values: [],
  },
  {
    key: 'language',
    values: [],
  },
  {
    key: 'marketName',
    values: [],
  },
  {
    key: 'state',
    values: [],
  },
  {
    key: 'loa',
    values: [],
  },
];

const filtersContext = createContext<FiltersContextValue | null>(null);

export interface FilterProviderProps {
  filterState: FilterState;
  setFilterState: (state: FilterState) => void;
  setViewDetails: (viewDetails: any) => void;
  clearPaginationData: () => void;
  savedViews: ViewListDataType[];
  setToastMessage: (message: string, msgType: toastMessageType) => void;
  closingMonth: string;
}

export const FiltersProvider: React.FC<PropsWithChildren<FilterProviderProps>> = ({
  children,
  filterState,
  setViewDetails,
  setToastMessage,
}) => {
  const [_filterState, _setFilterState] = useState<FilterState>(filterState);

  const [selectedFilter, setSelectedFilter] = useState<FilterKey>(filterState[0]?.key);

  const onSelect = useCallback(
    (value: string, checked: boolean, code: string) => {
      _setFilterState((currentState) =>
        currentState.map((state) => {
          if (state.key !== selectedFilter) return state;
          if (checked) {
            return {
              ...state,
              values: Array.from(new Set([...state.values, code])),
            };
          }

          return {
            ...state,
            values: state.values.filter((val) => val !== code),
          };
        })
      );
    },
    [selectedFilter]
  );

  const activeFilterState = useMemo(
    () => _filterState.find((st) => st.key === selectedFilter),
    [selectedFilter, _filterState]
  );

  const value = useMemo<FiltersContextValue>(() => {
    return {
      filterState: _filterState,
      activeFilterState,
      setFilterState: _setFilterState,
      selectedFilter,
      setSelectedFilter,
      onSelect,
      setViewDetails,
      setToastMessage,
    };
  }, [_filterState, activeFilterState, selectedFilter, onSelect]);

  useEffect(() => {
    _setFilterState(filterState);
  }, [filterState]);

  return <filtersContext.Provider value={value}>{children}</filtersContext.Provider>;
};

export const useFiltersContext = () => {
  const value = useContext(filtersContext);
  if (!value) throw new Error('Cannot use useFiltersContext outside of FiltersProvider !');

  return value;
};
